import { ThemeProvider } from '@servicetitan/design-system';
import { FC, ReactNode, useMemo } from 'react';
import { buildColorPaletteTheme, ColorPaletteFamilyShades } from '../../utils/color-palette-theme';

/*
 * Spike finding (Phase 2 task 1 / ARCH-SET-PALETTE-001 "Application mechanism"):
 *
 * The architecture doc assumed mounting Anvil2's `ThemeProvider` (`@servicetitan/anvil2`) would
 * re-theme buttons/nav/panels via its semantic-token override. Runtime inspection of the
 * installed `@servicetitan/design-system@14.5.1` (the actual library this portal's Button,
 * SideNav, and Card components come from — see portal-layout.tsx, portal-layout-menu.tsx)
 * showed those components read a distinct CSS-custom-property namespace (`--colorsSurfacePrimary`,
 * `--colorsTextPrimary`, `--colorsHoverPrimary`, `--colorsSurfacePrimarySubdued`, ...) that is
 * populated by a *separate*, Anvil1-native `ThemeProvider` also exported from
 * `@servicetitan/design-system` (backed by `@servicetitan/anvil-themes`'s `theme` shape), not by
 * Anvil2's `ThemeProvider` (which writes an entirely different `--kebab-case` namespace derived
 * from `@servicetitan/hammer-token`'s `Token` schema, e.g. `--button-primary-background-color`).
 *
 * Additionally, even within Anvil2, Button's primary color is a *component*-tier token
 * (`ButtonPrimaryBackgroundColor`), not the *semantic*-tier token
 * (`BackgroundColorPrimary`) the architecture doc named — confirming the doc's assumed token
 * names needed runtime verification before use, exactly as this spike task was scoped to check.
 *
 * Net result: this mount wraps Anvil1's `ThemeProvider` (from `@servicetitan/design-system`),
 * not Anvil2's, since that is the ThemeProvider whose token namespace the portal's actual
 * rendered components consume. Mounted once here, at the portal application's top-level layout
 * component (PortalLayout), per ARCH-SET-PALETTE-001's "single mount point" decision — this
 * satisfies the architectural intent (one override point for buttons/nav/panels spanning MFE
 * boundaries) even though the concrete provider differs from the doc's literal wording.
 */

export interface ColorPaletteThemeProviderProps {
    /** Resolved family shades, or undefined/null when no palette is selected (unset). */
    shades: ColorPaletteFamilyShades | undefined | null;
    children?: ReactNode;
}

export const ColorPaletteThemeProvider: FC<ColorPaletteThemeProviderProps> = ({
    children,
    shades,
}) => {
    /*
     * Memoized on the individual shade values (not the `shades` object reference) because this
     * mounts at AppRouter (app.tsx), which re-renders on every route navigation — without this,
     * buildColorPaletteTheme would reallocate the theme object on every navigation even though
     * the resolved palette is stable for the whole session in the common case.
     */
    const theme = useMemo(
        () => (shades ? buildColorPaletteTheme(shades) : undefined),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [shades?.primary, shades?.secondary, shades?.lightest]
    );

    /*
     * Unset selection -> render children directly, without mounting Anvil1's ThemeProvider at
     * all. Passing `theme={undefined}` to ThemeProvider is NOT equivalent to omitting it: Anvil1's
     * ThemeProvider still writes its own default theme's full CSS-custom-property set into the
     * `#themeVariables` <style> tag (including --colorsSurfacePrimarySubdued), which is always
     * present once mounted. color-palette-panel.less's `var(--colorsSurfacePrimarySubdued,
     * @fallback)` then picks up that default-theme value instead of falling back to @fallback —
     * so unset tenants got a light-blue-tinted panel/icons instead of today's plain white/neutral
     * look, even though buttons/nav happened to still match blue (Anvil1's default primary shade
     * coincides with today's hardcoded blue). Not mounting ThemeProvider at all when unset means
     * no --colorsX vars exist, so every consumer (LESS mixins and hardcoded Anvil1 component
     * styles alike) falls through to its non-CSS-var default, matching today's actual behavior.
     */
    if (!shades) {
        return children;
    }

    return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
